import { Fragment, useMemo, useState } from 'react';
import {
  FormatAlignCenter,
  FormatAlignJustify,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatClear,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  FormatSize,
  Highlight,
  HorizontalRule,
  Redo,
  Rule,
  StrikethroughS,
  Undo,
} from '@mui/icons-material';
import { Divider, IconButton, Popover, Tooltip } from '@mui/material';
import { Stack } from '@mui/system';
import { useCurrentEditor } from '@tiptap/react';

function MeetingTiptapMenu() {
  const { editor } = useCurrentEditor();
  const [optionsAnchorEl, setOptionsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openOption, setOpenOption] = useState('');

  const handleOpenOptions = (event: React.MouseEvent<HTMLButtonElement>, option: string) => {
    setOptionsAnchorEl(event.currentTarget);
    setOpenOption(option);
  };

  const items = useMemo(() => {
    if (!editor) {
      return [];
    }
    return [
      {
        title: 'Text Styling',
        icon: <FormatBold />,
        children: [
          {
            icon: <FormatBold />,
            title: 'Bold',
            action: () => editor.chain().focus().toggleBold().run(),
            isActive: () => editor.isActive('bold'),
          },
          {
            icon: <FormatItalic />,
            title: 'Italic',
            action: () => editor.chain().focus().toggleItalic().run(),
            isActive: () => editor.isActive('italic'),
          },
          {
            icon: <StrikethroughS />,
            title: 'Strike',
            action: () => editor.chain().focus().toggleStrike().run(),
            isActive: () => editor.isActive('strike'),
          },
          {
            icon: <Highlight />,
            title: 'Highlight',
            action: () => editor.chain().focus().toggleHighlight().run(),
            isActive: () => editor.isActive('highlight'),
          },
        ],
      },
      {
        title: 'Text Format',
        icon: <FormatSize />,
        children: [
          {
            icon: 'H1',
            title: 'Heading 1',
            action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
            isActive: () => editor.isActive('heading', { level: 1 }),
          },
          {
            icon: 'H2',
            title: 'Heading 2',
            action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
            isActive: () => editor.isActive('heading', { level: 2 }),
          },
          {
            icon: 'H3',
            title: 'Heading 3',
            action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
            isActive: () => editor.isActive('heading', { level: 3 }),
          },
          {
            icon: 'P',
            title: 'Paragraph',
            action: () => editor.chain().focus().setParagraph().run(),
            isActive: () => editor.isActive('paragraph'),
          },
        ],
      },
      {
        title: 'Alignment',
        icon: <FormatAlignCenter />,
        children: [
          {
            icon: <FormatAlignLeft />,
            title: 'Format Left',
            action: () => editor.chain().focus().setTextAlign('left').run(),
            isActive: () => editor.isActive({ textAlign: 'left' }),
          },
          {
            icon: <FormatAlignCenter />,
            title: 'Format Center',
            action: () => editor.chain().focus().setTextAlign('center').run(),
            isActive: () => editor.isActive({ textAlign: 'center' }),
          },
          {
            icon: <FormatAlignRight />,
            title: 'Format Right',
            action: () => editor.chain().focus().setTextAlign('right').run(),
            isActive: () => editor.isActive({ textAlign: 'right' }),
          },
          {
            icon: <FormatAlignJustify />,
            title: 'Format Justify',
            action: () => editor.chain().focus().setTextAlign('justify').run(),
            isActive: () => editor.isActive({ textAlign: 'justify' }),
          },
        ],
      },
      {
        type: 'divider',
      },
      {
        icon: <FormatListBulleted />,
        title: 'Bullet List',
        action: () => editor.chain().focus().toggleBulletList().run(),
        isActive: () => editor.isActive('bulletList'),
      },
      {
        icon: <FormatListNumbered />,
        title: 'Ordered List',
        action: () => editor.chain().focus().toggleOrderedList().run(),
        isActive: () => editor.isActive('orderedList'),
      },
      {
        icon: <Rule />,
        title: 'Task List',
        action: () => editor.chain().focus().toggleTaskList().run(),
        isActive: () => editor.isActive('taskList'),
      },
      {
        type: 'divider',
      },
      {
        icon: <FormatQuote />,
        title: 'Blockquote',
        action: () => editor.chain().focus().toggleBlockquote().run(),
        isActive: () => editor.isActive('blockquote'),
      },
      {
        icon: <HorizontalRule />,
        title: 'Horizontal Rule',
        action: () => editor.chain().focus().setHorizontalRule().run(),
      },
      {
        icon: <FormatClear />,
        title: 'Clear Format',
        action: () => editor.chain().focus().clearNodes().unsetAllMarks().run(),
      },
      {
        type: 'divider',
      },
      {
        icon: <Undo />,
        title: 'Undo',
        action: () => editor.chain().focus().undo().run(),
      },
      {
        icon: <Redo />,
        title: 'Redo',
        action: () => editor.chain().focus().redo().run(),
      },
    ];
  }, [editor]);

  return (
    <Stack
      alignItems="center"
      direction="row"
      flexWrap="wrap"
      gap={0.25}
      justifyContent="flex-start"
      width="fit-content"
    >
      {items.map((item: any, index: number) => (
        <Fragment key={index}>
          {item.type === 'divider' ? (
            <Divider flexItem orientation="vertical" sx={{ borderRightWidth: 'medium' }} variant="middle" />
          ) : item.children ? (
            <>
              <IconButton
                aria-describedby={`options-popover-${item.title}`}
                color="inherit"
                onClick={(e) => { handleOpenOptions(e, item.title); }}
              >
                {item.icon}
              </IconButton>
              <Popover
                anchorEl={optionsAnchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                id={`options-popover-${item.title}`}
                onClose={() => {
                  setOptionsAnchorEl(null);
                }}
                open={Boolean(openOption === item.title && optionsAnchorEl)}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                {item.children.map((child: any) => (
                  <Tooltip arrow key={child.title} placement="top" title={child.title}>
                    <IconButton
                      aria-label={child.title}
                      color={child.isActive?.() ? 'primary' : 'inherit'}
                      onClick={child.action}
                      sx={{}}
                    >
                      {child.icon}
                    </IconButton>
                  </Tooltip>
                ))}
              </Popover>
            </>
          ) : (
            <Tooltip arrow placement="top" title={item.title}>
              <IconButton
                aria-label={item.title}
                color={item.isActive?.() ? 'primary' : 'inherit'}
                onClick={item.action}
                sx={{}}
              >
                {item.icon}
              </IconButton>
            </Tooltip>
          )}
        </Fragment>
      ))}
    </Stack>
  );
}

export default MeetingTiptapMenu;
